/**
 * 单个阶段节点组件
 */

import { User, Clock } from 'lucide-react'
import { type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { getStageDisplayName, type TimeStatus } from '../workflow-constants'
import { formatStageTime } from '../workflow-utils'

import { StageStatusIcon, type StageStatus } from './stage-status-badge'

import type { WorkflowStage } from '@company/shared'

// ==================== 类型定义 ====================

interface StageNodeProps {
  stage: WorkflowStage
  index: number
  status: StageStatus
  timeStatus?: TimeStatus | null
  ownerName?: string | null
  startedAt?: string | null
  isLast?: boolean
  actions?: ReactNode
  className?: string
}

// ==================== 组件实现 ====================

export function StageNode({
  stage,
  index,
  status,
  timeStatus,
  ownerName,
  startedAt,
  isLast = false,
  actions,
  className,
}: StageNodeProps) {
  const stageName = getStageDisplayName(stage)

  return (
    <div className={cn('flex items-start gap-3', className)}>
      {/* 状态图标 */}
      <StageStatusIcon status={status} timeStatus={timeStatus} index={index} size="lg" />

      {/* 阶段内容 */}
      <div className="min-w-0 flex-1">
        {/* 阶段名称和状态标签 */}
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-base font-medium',
              status === 'completed' && 'text-green-700 dark:text-green-400',
              status === 'current' && 'text-foreground',
              status === 'pending' && 'text-muted-foreground'
            )}
          >
            {stageName}
          </span>
          {status === 'current' && (
            <span className="rounded-full bg-blue-500 px-2.5 py-1 text-xs font-medium text-white">
              当前阶段
            </span>
          )}
        </div>

        {/* 负责人信息 */}
        {ownerName && (
          <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-sm">
            <User className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{ownerName}</span>
          </div>
        )}

        {/* 开始时间（仅当前阶段显示） */}
        {status === 'current' && startedAt && (
          <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
            <Clock className="h-3 w-3" aria-hidden="true" />
            <span>开始于 {formatStageTime(startedAt)}</span>
          </div>
        )}

        {/* 操作按钮 */}
        {actions && <div className="mt-2">{actions}</div>}
      </div>

      {/* 连接线 */}
      {!isLast && (
        <div
          className={cn(
            'mt-4 h-px w-4',
            status === 'completed' ? 'bg-green-300 dark:bg-green-700' : 'bg-muted'
          )}
          aria-hidden="true"
        />
      )}
    </div>
  )
}

/**
 * 阶段连接线
 */
export function StageConnector({
  completed,
  className,
}: {
  completed: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'h-px w-4',
        completed ? 'bg-green-300 dark:bg-green-700' : 'bg-muted',
        className
      )}
      aria-hidden="true"
    />
  )
}
